import { User, useFeatureFlag } from 'configcat-react';
import { useMemo } from 'react';
import { useUserContext } from './useUserContext';

// The ConfigCat setting is named "paymentLinks", but its key is immutable once created and the SDK
// resolves flags by key.
export const PAYMENT_LINKS_FLAG_KEY = 'paymentLinksOnboardingBanner';

/**
 * Resolves the Payment Links flag for the current tenant and user. It gates the dashboard
 * onboarding banner and the PaySuite screens (onboarding, disputes, reporting, transactions).
 *
 * Only call this from a component mounted under a configured `FeatureFlagProvider`; every other
 * consumer reads the resolved value through `usePaymentLinksEnabled()`. While the config is
 * loading the flag is `false`, so nothing gated by it renders before the value is known.
 */
export function usePaymentLinksFlag(): { isEnabled: boolean; isLoading: boolean } {
  const { data } = useUserContext();

  const targetingUser = useMemo(() => {
    const identifier = data?.userId ?? data?.internalUserId?.toString() ?? 'anonymous';

    return new User(identifier, undefined, undefined, {
      evoOrganisationId: data?.evoOrganisationId ?? '',
      organisationId: data?.organisationId?.toString() ?? '',
      companyId: data?.companyId?.toString() ?? '',
      role: data?.role ?? '',
    });
  }, [data]);

  const { value, loading } = useFeatureFlag(PAYMENT_LINKS_FLAG_KEY, false, targetingUser);

  return { isEnabled: !loading && value, isLoading: loading };
}
